import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PhoneOff, DollarSign, CalendarCheck, TrendingUp, ArrowRight, Bot, Zap } from "lucide-react";
import TwilioStatusCard from "@/components/TwilioStatusCard";
import { useAuth } from "@/context/AuthContext";

const money = (n) => `$${Math.round(n).toLocaleString()}`;

function Stat({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <Card className="bg-slate-900 border-slate-800 p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">{label}</p>
          <p className="font-display text-3xl font-extrabold text-white font-mono tracking-tight">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}

const urgencyStyle = {
  emergency: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  high: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  medium: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  low: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

export default function Overview() {
  const { org } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/analytics/overview").then(r => setStats(r.data)),
      api.get("/leads").then(r => setLeads(r.data)),
      api.get("/estimates").then(r => setEstimates(r.data)),
    ]).catch(console.error);
  }, []);

  const recent = leads.slice(0, 5);
  const upcoming = estimates.filter(e => new Date(e.scheduled_at) > new Date()).slice(0, 4);

  return (
    <div className="p-6 lg:p-10 max-w-[1600px] mx-auto">
      <header className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-2">Command Center</p>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Overview</h1>
        </div>
        <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/15">
          <span className="relative flex w-2 h-2 mr-2"><span className="pulse-dot text-emerald-400 absolute" /><span className="relative rounded-full bg-emerald-400 w-2 h-2" /></span>
          AI Active
        </Badge>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat testId="stat-recovered-revenue" icon={DollarSign} accent="bg-emerald-500/15 text-emerald-400" label="Recovered Revenue" value={stats ? money(stats.recovered_revenue) : "—"} sub={stats ? `${stats.won_deals} deals won` : ""} />
        <Stat testId="stat-missed-captured" icon={PhoneOff} accent="bg-orange-500/15 text-orange-400" label="Missed Calls Captured" value={stats?.total_missed ?? "—"} sub={`${stats?.recovered_leads ?? 0} recovered`} />
        <Stat testId="stat-estimates-booked" icon={CalendarCheck} accent="bg-sky-500/15 text-sky-400" label="Estimates Booked" value={stats?.booked_estimates ?? "—"} sub={`${stats?.upcoming_estimates ?? 0} upcoming`} />
        <Stat testId="stat-conversion" icon={TrendingUp} accent="bg-amber-500/15 text-amber-400" label="Conversion Rate" value={stats ? `${stats.conversion_rate}%` : "—"} sub={stats?.avg_response_seconds != null ? `Avg reply ${stats.avg_response_seconds}s` : "No AI replies yet"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 bg-slate-900 border-slate-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-xl font-semibold">Live Lead Stream</h2>
              <p className="text-xs text-slate-500 mt-0.5">Most recent missed calls & AI responses</p>
            </div>
            <Button variant="ghost" onClick={() => navigate("/app/inbox")} data-testid="overview-view-all" className="text-orange-400 hover:text-orange-300 hover:bg-slate-800">
              View all <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="space-y-2">
            {recent.length === 0 && <p className="text-sm text-slate-500 py-8 text-center">No leads yet.</p>}
            {recent.map((l) => (
              <div key={l.id} onClick={() => navigate(`/app/chat/${l.id}`)} className="flex items-center gap-4 p-3 rounded-lg bg-slate-800/40 border border-slate-800 hover:border-orange-500/40 hover:bg-slate-800/70 cursor-pointer transition-all" data-testid={`overview-lead-${l.id}`}>
                <div className="w-10 h-10 rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center shrink-0">
                  <PhoneOff className="w-4 h-4 text-orange-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-semibold text-white truncate">{l.caller_name}</p>
                    <Badge className={`text-xs border ${urgencyStyle[l.urgency]}`}>{l.urgency}</Badge>
                  </div>
                  <p className="text-xs text-slate-400 font-mono truncate">{l.caller_phone} · {l.issue}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-sm font-bold text-emerald-400">${l.estimated_value.toLocaleString()}</p>
                  <p className="text-xs text-slate-500 capitalize">{l.status}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="bg-slate-900 border-slate-800 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-4 h-4 text-orange-400" />
            <h2 className="font-display text-xl font-semibold">Upcoming Inspections</h2>
          </div>
          <div className="space-y-3">
            {upcoming.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">No upcoming inspections.</p>}
            {upcoming.map((e) => (
              <div key={e.id} className="p-3 rounded-lg bg-slate-800/40 border border-slate-800" data-testid={`overview-estimate-${e.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold text-sm text-white">{e.caller_name}</p>
                  <span className="font-mono text-xs text-orange-400">{new Date(e.scheduled_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                </div>
                <p className="text-xs text-slate-400 truncate">{e.address}</p>
                <p className="text-xs text-slate-500 mt-1 font-mono">{new Date(e.scheduled_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {e.estimator_name}</p>
              </div>
            ))}
          </div>
          <Button onClick={() => navigate("/app/calendar")} data-testid="overview-view-calendar" variant="outline" className="w-full mt-4 border-slate-700 bg-slate-800/60 hover:bg-slate-800 text-slate-100">
            Open calendar
          </Button>
        </Card>
      </div>

      <Card className="mt-4 bg-gradient-to-r from-orange-500/10 to-emerald-500/10 border-orange-500/30 p-5 flex items-center gap-4">
        <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center"><Bot className="w-5 h-5 text-orange-400" /></div>
        <div className="flex-1">
          <p className="font-semibold text-white">AI response time avg <span className="font-mono text-orange-400">{stats?.avg_response_seconds != null ? `${stats.avg_response_seconds}s` : "—"}</span></p>
          <p className="text-xs text-slate-400">Claude Sonnet 5 is handling every missed call in near-real time — homeowners get replied to before your competitors even see the ring.</p>
        </div>
      </Card>

      <div className="mt-4">
        {!org?.is_demo && <TwilioStatusCard />}
      </div>
    </div>
  );
}
